import axios from 'axios';
import { CardCreationRequest } from '@/types/card';

// Remove /api/v1 from base URL if it's included
const baseURL = import.meta.env.VITE_API_URL.endsWith('/api/v1') 
  ? import.meta.env.VITE_API_URL.slice(0, -7) 
  : import.meta.env.VITE_API_URL;

const api = axios.create({
  baseURL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  }
});

// Add request interceptor for auth and CORS
api.interceptors.request.use(async (config) => {
  try {
    // Add auth token
    const token = await window.Clerk?.session?.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // console.log('Request config:', {
    //   url: `${config.baseURL}${config.url}`,
    //   method: config.method,
    //   headers: config.headers
    // });
    
    return config;
  } catch (error) {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
});

// Simple error logging
api.interceptors.response.use(
  response => response,
  error => {
    console.error('API Error:', {
      status: error.response?.status,
      data: error.response?.data,
      headers: error.response?.headers
    });
    return Promise.reject(error);
  }
);

// Add debug logging for all environments
api.interceptors.request.use((config) => {
  // console.log('Making request to:', `${config.baseURL}${config.url}`);
  // console.log('Request headers:', config.headers);
  // console.log('Request method:', config.method);
  // console.log('Request data:', config.data);
  return config;
});

api.interceptors.response.use(
  (response) => {
    console.log('Response received: ', {
      status: response.status,
      headers: response.headers,
      data: response.data
    });
    return response;
  },
  (error) => {
    console.error('API Error Details:', {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      data: error.response?.data,
      headers: error.response?.headers,
      message: error.message,
      config: error.config
    });
    return Promise.reject(error);
  }
);

// Add this interface near the top of the file
interface TokenBalanceResponse {
  data: {
    remaining: number;
    total: number;
    resetIn: number;
    tier: string;
  };
}

// Add this near other interfaces
interface UserTierResponse {
  data: {
    tier: string;
  };
}

// API endpoints
const endpoints = {
  feed: '/api/v1/feed',
  cards: {
    generate: '/api/v1/cards/generate',
    like: (id: number) => `/api/v1/cards/${id}/like`,
    comment: (id: number) => `/api/v1/cards/${id}/comments`,
    get: (id: number) => `/api/v1/cards/${id}`,
    updateTitle: (id: number) => `/api/v1/cards/${id}/title`,
    updateDescription: (id: number) => `/api/v1/cards/${id}/description`,
    updateImage: (id: number) => `/api/v1/cards/${id}/image`,
  },
  images: {
    get: (key: string) => `/api/images/${key.replace(/^\/+/, '')}`, // Remove leading slashes
  },
  tokens: {
    balance: '/api/v1/tokens/balance',
    purchase: '/api/v1/tokens/purchase'
  },
  users: {
    stats: '/api/v1/users/stats',
    profile: (username: string) => `/api/v1/users/${username}/profile`,
    sync: '/api/v1/users/sync',
    isAdmin: '/api/v1/users/is-admin',
    toggleNotifications: '/api/v1/users/notifications',
    getNotifications: '/api/v1/users/notifications',
    getTier: '/api/v1/users/tier',
  },
  blueprints: {
    test: '/api/v1/blueprints/test',
    create: '/api/v1/blueprints',
    get: (id: string) => `/api/v1/blueprints/${id}`,
    update: (id: string) => `/api/v1/blueprints/${id}`,
    delete: (id: string) => `/api/v1/blueprints/${id}`,
    list: '/api/v1/blueprints'
  },
  decks: {
    list: '/api/v1/decks',
    create: '/api/v1/decks',
    get: (id: string) => `/api/v1/decks/${id}`,
    update: (id: number) => `/api/v1/decks/${id}`,
    delete: (id: number) => `/api/v1/decks/${id}`,
    addCard: (id: number) => `/api/v1/decks/${id}/cards`,
    removeCard: (id: number, cardId: number) => `/api/v1/decks/${id}/cards/${cardId}`,
  },
  themes: {
    list: '/api/v1/themes/list',
    get: (id: string) => `/api/v1/themes/${id}`,
    randomTheme: '/api/v1/genesis/random-theme',
    lostGames: '/api/v1/genesis/lost-games',
    themeDetails: (id: string) => `/api/v1/genesis/themes/${id}/details`,
    cardTypes: (id: string) => `/api/v1/genesis/themes/${id}/card-types`,
    artStyles: (id: string) => `/api/v1/genesis/themes/${id}/art-styles`,
    createDeck: (id: string) => `/api/v1/genesis/themes/${id}/create-deck`,
    generateFromInput: '/api/v1/genesis/generate-theme',
  }
};

// Add debug logging for development
if (import.meta.env.DEV) {
  api.interceptors.request.use((config) => {
    return config;
  });
}

// API methods with debug logging
export const fetchFeed = async (params = {}) => {
  console.log('Fetching feed with URL:', endpoints.feed, 'and params:', params);
  const response = await api.get(endpoints.feed, { params });
  console.log('Feed response:', response);
  return response;
};

export const generateCard = async (data: any) => {
  return api.post(endpoints.cards.generate, data);
};

export const likeCard = async (cardId: number) => {
  return api.post(endpoints.cards.like(cardId));
};

export const unlikeCard = async (cardId: number) => {
  return api.delete(endpoints.cards.like(cardId));
};

export const addComment = async (cardId: number, content: string) => {
  return api.post(endpoints.cards.comment(cardId), { content });
};

export const updateCardTitle = async (cardId: number, data: any) => {
  console.log('Updating title with data:', data);
  return api.patch(`${endpoints.cards.updateTitle(cardId)}`, {
    universeId: data.universeId,
    universe: data.universe,
    fields: data.fields,
    language: data.language,
    playerName: data.playerName
  });
};

export const updateCardDescription = async (cardId: number, data: any) => {
  console.log('Updating description with data:', data);
  return api.patch(`${endpoints.cards.updateDescription(cardId)}`, {
    universeId: data.universeId,
    universe: data.universe,
    fields: data.fields,
    language: data.language,
    playerName: data.playerName
  });
};

export const updateCardImage = async (cardId: number, data: any) => {
  console.log('Updating image with data:', data);
  return api.patch(`${endpoints.cards.updateImage(cardId)}`, {
    universeId: data.universeId,
    universe: data.universe,
    fields: data.fields,
    language: data.language,
    playerName: data.playerName
  });
};

export const getTokenBalance = async () => {
  return api.get<TokenBalanceResponse>(endpoints.tokens.balance);
};

export const purchaseTokens = async (amount: number, paymentIntentId: string) => {
  return api.post(endpoints.tokens.purchase, { amount, paymentIntentId });
};

export const getDebugToken = async () => {
  const token = await window.Clerk?.session?.getToken();
  console.log('Current API token:', token);
  return token;
};

export const getUserStats = async () => {
  return api.get(endpoints.users.stats);
};

export const testBlueprint = async (data: any) => {
  const response = await api.post(endpoints.blueprints.test, data);
  return response.data;
};

export const createBlueprint = async (data: any) => {
  return api.post(endpoints.blueprints.create, data);
};

export const getBlueprint = async (id: string) => {
  console.log('Fetching blueprint:', id);
  try {
    const response = await api.get(endpoints.blueprints.get(id));
    console.log('Blueprint response:', response);
    return response;
  } catch (error) {
    console.error('Blueprint fetch error:', error);
    throw error;
  }
};

export const updateBlueprint = async (id: number, data: any) => {
  try {
    const token = await window.Clerk?.session?.getToken();
    const response = await api.patch(endpoints.blueprints.update(id.toString()), data, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Blueprint update error:', error);
    throw error;
  }
};

export const deleteBlueprint = async (id: string) => {
  return api.delete(endpoints.blueprints.delete(id));
};

export const listBlueprints = async (showAll: boolean = false) => {
  console.log('Fetching blueprints...');
  try {
    const response = await api.get(endpoints.blueprints.list, {
      params: { showAll }
    });
    console.log('Raw blueprints response:', response);
    
    // Extract the nested data array
    const blueprints = response.data?.data || [];
    
    // Return consistent structure
    return {
      data: blueprints,
      status: response.status
    };
  } catch (error) {
    console.error('Blueprint fetch error:', error);
    throw error;
  }
};

// Add new function to check admin status
export const checkIsAdmin = async () => {
  try {
    const response = await api.get(endpoints.users.isAdmin);
    return response.data.isAdmin;
  } catch (error) {
    console.error('Error checking admin status:', error);
    return false;
  }
};

export const listDecks = async (showAll: boolean = false) => {
  console.log('Fetching decks with showAll:', showAll);
  try {
    const response = await api.get(endpoints.decks.list, {
      params: { showAll }
    });
    console.log('Decks response:', response);
    return response;
  } catch (error) {
    console.error('Error fetching decks:', error);
    throw error;
  }
};

export const createDeck = async (data: any) => {
  return api.post(endpoints.decks.create, data);
};

export const getDeck = async (deckId: string) => {
  try {
    const response = await api.get(endpoints.decks.get(deckId));
    
    // Ensure we're returning the correct data structure
    if (response.data?.data) {
      // Fix any malformed image URLs
      const cards = response.data.data.cards?.map((card: any) => ({
        ...card,
        imageUrl: card.imageUrl?.replace('/api/images//api/v1', '/api/images/')
      })) || [];

      return {
        data: {
          ...response.data.data,
          cards
        }
      };
    }
    
    return response;
  } catch (error) {
    console.error('Error fetching deck:', error);
    throw error;
  }
};

export const createCard = async (data: CardCreationRequest) => {
  try {
    console.log('Creating card with data:', data);
    const response = await api.post('/api/v1/cards/generate', data);
    console.log('Card creation raw response:', response);
    console.log('Card creation data structure:', response.data);
    return response.data;
  } catch (error) {
    console.error('Card creation error:', error);
    throw error;
  }
};

export const getRandomTheme = async (params?: { userId?: string }) => {
  try {
    const response = await api.get(endpoints.themes.randomTheme, {
      params: params
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching random theme:', error);
    throw error;
  }
};

export const getThemeDetails = async (themeId: string) => {
  try {
    const response = await api.get(endpoints.themes.themeDetails(themeId));
    return response.data;
  } catch (error) {
    console.error('Error fetching theme details:', error);
    throw error;
  }
};

export const getThemeCardTypes = async (themeId: string) => {
  try {
    const response = await api.get(endpoints.themes.cardTypes(themeId));
    return response.data;
  } catch (error) {
    console.error('Error fetching card types:', error);
    throw error;
  }
};

export const getLostGames = async () => {
  try {
    const response = await api.get(endpoints.themes.lostGames);
    return response.data;
  } catch (error) {
    console.error('Error fetching lost games:', error);
    throw error;
  }
};

export const getThemeArtStyles = async (themeId: string) => {
  try {
    const response = await api.get(endpoints.themes.artStyles(themeId));
    return response.data;
  } catch (error) {
    console.error('Error fetching art styles:', error);
    throw error;
  }
};

export const createThemeDeck = async (themeId: string, artStyleId: string) => {
  try {
    const response = await api.post(endpoints.themes.createDeck(themeId), {
      themeId,
      artStyleId
    });
    return response.data;
  } catch (error) {
    console.error('Error creating theme deck:', error);
    throw error;
  }
};

export const listThemes = async () => {
  console.log('Fetching themes...');
  try {
    const response = await api.get(endpoints.themes.list);
    console.log('Themes response:', response);
    return response;
  } catch (error) {
    console.error('Error fetching themes:', error);
    throw error;
  }
};

export const getTheme = async (id: string) => {
  console.log('Fetching theme:', id);
  try {
    const response = await api.get(endpoints.themes.get(id));
    console.log('Theme response:', response);
    return response;
  } catch (error) {
    console.error('Error fetching theme:', error);
    throw error;
  }
};

export const generateThemeFromInput = async (input: string) => {
  try {
    const response = await api.post(endpoints.themes.generateFromInput, {
      input
    });
    return response.data;
  } catch (error) {
    console.error('Error generating theme from input:', error);
    throw error;
  }
};

export const toggleNotifications = async (enabled: boolean) => {
  try {
    const response = await api.patch(endpoints.users.toggleNotifications, { enabled });
    return response.data;
  } catch (error) {
    console.error('Error toggling notifications:', error);
    throw error;
  }
};

export const getNotificationsStatus = async () => {
  try {
    const response = await api.get(endpoints.users.getNotifications);
    return response.data;
  } catch (error) {
    console.error('Error getting notifications status:', error);
    throw error;
  }
};

export const getUserTier = async (): Promise<string> => {
  try {
    console.log('Fetching user tier...');
    const response = await api.get<UserTierResponse>(endpoints.users.getTier);
    console.log('User tier response:', response.data);
    return response.data.data.tier;
  } catch (error) {
    console.error('Error getting user tier:', error);
    return 'free'; // Default to free tier on error
  }
};

export default api; 